import { DEFAULT_MIRA_MODE } from "@/data/models/MiraMode";
import {
  ClientContractData,
  ClientProductsValue,
  MASTER_DATA_FIELDS,
  MasterDataField,
  NotLiveReason,
} from "@/data/models/ClientAdministrationRow";
import {
  CLIENT_SETUP_LICENCE_KEYS,
  CLIENT_SETUP_REFUSALS,
  ClientSetupDeviceUpdates,
  ClientSetupFirstUser,
  ClientSetupFunctions,
  ClientSetupGroupKind,
  ClientSetupLicenceKey,
  ClientSetupLicences,
  ClientSetupOptions,
  ClientSetupProjectOrganisation,
  ClientSetupRefusal,
  ClientSetupRequest,
  ClientSetupValidationLevel,
  ProjectOrganisationApp,
} from "@/data/models/ClientSetup";
import { MasterDataFieldError, MasterDataForm, masterDataFieldError, masterDataValue } from "@/utils/ClientMasterData";
import { PartnerSystemChoice } from "@/data/models/ClientPartner";
import { isPartnerEmail, isValidPartnerAddress, proposedPartnerEmail } from "@/utils/ClientPartners";
import { MAY_BE_NOWHERE, PROJECT_ORGANISATION_QUESTIONS } from "@/utils/ClientProjectOrganisation";

/**
 * The guided setup of a new client (Track #2610082): the form it fills step by step, what each step
 * needs before WEITER, and the request ANLEGEN sends. Pure. IRES Cloud repeats every rule
 * (`ClientCreationFields.ts`); these give the steps their messages. Docs: `docs/admin/client-setup.md`.
 */

/** The steps, in order. */
export const SETUP_STEPS = [
  "masterData",
  "products",
  "projectTypes",
  "projectOrganisation",
  "functions",
  "licences",
  "contracts",
  "users",
  "externalSystems",
  "summary",
] as const;

export type SetupStep = typeof SETUP_STEPS[number];

/** The steps a draft shows: Externe Systeme only with API (#2610084, merge 4). */
export function setupSteps(draft: ClientSetupDraft): SetupStep[] {
  return SETUP_STEPS.filter((step) => step !== "externalSystems" || draft.products.interface);
}

/** The one external system the setup can create, as its step edits it (the NEU dialog's fields). */
export interface SetupExternalSystemForm {
  name: string;
  /** Null until typed by hand: until then it follows the Kürzel and the name. */
  typedEmail: string | null;
  system: PartnerSystemChoice;
  signIn: "API_KEY" | "SIGN_IN";
  baseUrl: string;
}

// The questions and the Nie rule live in the leaf the Projektorganisation card shares (2026-10-29).
export { MAY_BE_NOWHERE, PROJECT_ORGANISATION_QUESTIONS };

/** The shortest password the setup accepts, for the client account and the first user. */
export const MIN_PASSWORD_LENGTH = 8;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The most of one licence IRES Cloud takes; more is a typing slip. */
export const MAX_LICENCE_COUNT = 9999;

/** The first user's form: "" for nothing typed, null for no validation level chosen yet. */
export type FirstUserForm = Omit<ClientSetupFirstUser, "validationLevel"> & { validationLevel: ClientSetupValidationLevel | null };

/** Everything the setup asks, as the steps edit it. */
export interface ClientSetupDraft {
  masterData: MasterDataForm;
  email: string;
  /** False until the email is typed by hand: until then it follows the client number. */
  emailEdited: boolean;
  password: string;
  /** Null for live operation. */
  notLiveReason: NotLiveReason | null;
  groupKind: ClientSetupGroupKind;
  masterClientId: string | null;
  products: ClientProductsValue;
  projectTypeIds: number[];
  projectOrganisation: ClientSetupProjectOrganisation;
  functions: ClientSetupFunctions;
  /** The counts as typed; only those the products need are sent. */
  licences: Record<ClientSetupLicenceKey, string>;
  contracts: ClientContractData;
  deviceUpdates: ClientSetupDeviceUpdates;
  withFirstUser: boolean;
  firstUser: FirstUserForm;
  /** With API, for a client that is no member: whether one external system is created with it. */
  withExternalSystem: boolean;
  externalSystem: SetupExternalSystemForm;
  /** With API, for a member: the master's systems it is linked to. */
  linkedExternalSystemIds: string[];
}

/** A setup with nothing chosen: a standalone customer, every function off, no products. */
export function createClientSetupDraft(): ClientSetupDraft {
  const masterData = {} as MasterDataForm;
  for (const field of MASTER_DATA_FIELDS) {
    masterData[field] = "";
  }
  masterData.clientType = "CLIENT";

  return {
    masterData,
    email: "",
    emailEdited: false,
    password: "",
    notLiveReason: null,
    groupKind: "STANDALONE",
    masterClientId: null,
    products: { officeDeviceAssignment: false, project: false, work: false, interface: false },
    projectTypeIds: [],
    projectOrganisation: {
      newProjectsIn: ["PORTAL"],
      editProjectsIn: ["PORTAL"],
      newAppointmentsIn: ["PORTAL"],
      finaliseProjectsIn: ["PORTAL"],
    },
    functions: {
      enableTimeTracking: false,
      enableGeoEncoding: false,
      enableTracking: false,
      enableAllDevicesManagement: false,
      enableDeviceManagementTile: false,
      enableVehicleManagement: false,
      enableScheduling: false,
      enablePersonalChat: false,
      enableProjectChat: false,
      enableAiPredictions: false,
      renoActivationScope: "ACTIVE_AND_NEW_ONLY",
      // Nur IRES, what every client gets until IRES releases Mira to it (#2610141).
      miraMode: DEFAULT_MIRA_MODE,
    },
    // Nothing bought yet (D4: editable later), no contract, and IRES Admin's update default.
    licences: { basic: "0", damageAssessmentAndLeakDetection: "0", dryingAndRepair: "0" },
    contracts: {
      contractTypes: [],
      hasServiceContractForFirmwaresAndConfigs: false,
      hasServiceContractForFirmwaresAndConfigsSince: null,
      hasWarrantyExtensionForPanels: false,
      hasWarrantyExtensionForPanelsSince: null,
    },
    deviceUpdates: { updateFirmwareViaIotCondition: "ALWAYS", updateConfigViaIotCondition: "ALWAYS" },
    withFirstUser: false,
    firstUser: {
      firstName: "",
      lastName: "",
      nickname: "",
      staffNumber: "",
      emailAddress: "",
      temporaryPassword: "",
      validationLevel: null,
    },
    withExternalSystem: false,
    externalSystem: { name: "", typedEmail: null, system: "EXTERNAL", signIn: "API_KEY", baseUrl: "" },
    linkedExternalSystemIds: [],
  };
}

/** The new external system's sign-in address: as typed, else proposed from the Kürzel and its name. */
export function setupExternalSystemEmail(draft: ClientSetupDraft): string {
  const form = draft.externalSystem;
  return form.typedEmail ?? proposedPartnerEmail(masterDataValue("iresProjectNumberPrefix", draft.masterData.iresProjectNumberPrefix), form.name);
}

/** The new external system's fields that are not ready, by name; none without one. */
export function setupExternalSystemErrors(draft: ClientSetupDraft): Array<"name" | "email" | "baseUrl"> {
  if (!draft.withExternalSystem) {
    return [];
  }
  const errors: Array<"name" | "email" | "baseUrl"> = [];
  const form = draft.externalSystem;
  if (form.name.trim() === "" || form.name.trim().length > 100) {
    errors.push("name");
  }
  const email = setupExternalSystemEmail(draft).trim().toLowerCase();
  // Its own account: never the client's own address.
  if (!isPartnerEmail(email) || email === draft.email.trim().toLowerCase()) {
    errors.push("email");
  }
  if (form.system !== "NONE" && !isValidPartnerAddress(form.baseUrl)) {
    errors.push("baseUrl");
  }
  return errors;
}

/** The client account's address IRES Admin proposes: the client number at ires.de. */
export function defaultClientEmail(clientNumber: string): string {
  const number = clientNumber.trim().toLowerCase();
  return number === "" ? "" : `${number}@ires.de`;
}

/** Whether the text is an email address, as IRES Cloud checks it. */
export function isEmailAddress(text: string): boolean {
  return EMAIL_PATTERN.test(text.trim());
}

/** The key of a switch combination in the options' type map (office, project, work, interface). */
export function productsKey(products: ClientProductsValue): string {
  return [products.officeDeviceAssignment, products.project, products.work, products.interface]
    .map((on) => (on ? "1" : "0"))
    .join("");
}

/** The project types the products allow, as IRES Cloud served them; none before the options load. */
export function possibleProjectTypeIds(options: ClientSetupOptions | null, products: ClientProductsValue): number[] {
  return options?.possibleProjectTypeIds[productsKey(products)] ?? [];
}

/**
 * The Projektorganisation choices the products allow: IRES Office always, IRES Project, IRES Work and
 * the interface with their product. IRES Cloud's `projectOrganisationOptions`, which refuses anything else.
 */
export function projectOrganisationOptions(products: ClientProductsValue): ProjectOrganisationApp[] {
  const apps: ProjectOrganisationApp[] = [];
  if (products.project) {
    apps.push("PROJECT_MANAGER");
  }
  if (products.work) {
    apps.push("DEVICE_MANAGER");
  }
  apps.push("PORTAL");
  if (products.interface) {
    apps.push("API");
  }
  return apps;
}

function sorted(ids: number[]): number[] {
  return Array.from(new Set(ids)).sort((left, right) => left - right);
}

/**
 * The draft after the products changed: a type or an app a product no longer allows is dropped, and
 * what a product newly allows is added, as a product switched on later brings its types (#2610081).
 * Mutates the draft, which the page owns.
 */
export function applyProductsChange(
  draft: ClientSetupDraft,
  options: ClientSetupOptions | null,
  before: ClientProductsValue
): void {
  const possibleBefore = possibleProjectTypeIds(options, before);
  const possibleAfter = possibleProjectTypeIds(options, draft.products);
  draft.projectTypeIds = sorted([
    ...draft.projectTypeIds.filter((id) => possibleAfter.includes(id)),
    ...possibleAfter.filter((id) => !possibleBefore.includes(id)),
  ]);

  const offeredBefore = projectOrganisationOptions(before);
  const offeredAfter = projectOrganisationOptions(draft.products);
  for (const question of PROJECT_ORGANISATION_QUESTIONS) {
    const kept = draft.projectOrganisation[question].filter((option) => offeredAfter.includes(option));
    const added = offeredAfter.filter((option) => !offeredBefore.includes(option) && !kept.includes(option));
    draft.projectOrganisation[question] = offeredAfter.filter((option) => kept.includes(option) || added.includes(option));
  }
}

/** Switches a prerequisite turned off take their dependants with them: TRACKING with KARTE, the tile with Alle Geräte. */
export function applyFunctionDependencies(functions: ClientSetupFunctions): void {
  if (!functions.enableGeoEncoding) {
    functions.enableTracking = false;
  }
  if (!functions.enableAllDevicesManagement) {
    functions.enableDeviceManagementTile = false;
  }
}

/**
 * The licences the products need, so the Lizenzen step asks for their counts: B with IRES Office (GZ / GL)
 * or IRES Project, A and T with IRES Project. IRES Work adds none while its licence model is open (Taimoor,
 * 2026-10-23). IRES Cloud's `neededLicences`, which refuses a count for any other.
 */
export function neededLicences(products: ClientProductsValue): ClientSetupLicenceKey[] {
  return CLIENT_SETUP_LICENCE_KEYS.filter((key) => (key === "basic" ? products.officeDeviceAssignment || products.project : products.project));
}

/** A typed count as a whole number from 0 to {@link MAX_LICENCE_COUNT}, or null when it is not one. */
export function licenceCount(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const count = Number(trimmed);
  return count <= MAX_LICENCE_COUNT ? count : null;
}

/** Whether the client is a member, which follows its master's Projekttypen, Projektorganisation and Funktionen. */
export function followsMaster(draft: ClientSetupDraft): boolean {
  return draft.groupKind === "MEMBER";
}

/** Why a Mandant field cannot be used, as an i18n key suffix of `admin.clients.masterData.fieldErrors`. */
export function setupMasterDataError(field: MasterDataField, text: string): MasterDataFieldError {
  if ((field === "clientNumber" || field === "iresProjectNumberPrefix") && masterDataValue(field, text) === null) {
    return "required";
  }
  return masterDataFieldError(field, text);
}

/** The first user's fields that are not ready, by name. */
export function firstUserErrors(user: FirstUserForm): Array<keyof FirstUserForm> {
  const errors: Array<keyof FirstUserForm> = [];
  for (const field of ["firstName", "lastName", "nickname", "staffNumber"] as const) {
    if (user[field].trim() === "") {
      errors.push(field);
    }
  }
  // The IRES admin user's Kurzname is taken in every client.
  if (user.nickname.trim().toLowerCase() === "iresadmin") {
    errors.push("nickname");
  }
  if (!isEmailAddress(user.emailAddress)) {
    errors.push("emailAddress");
  }
  if (user.temporaryPassword.length < MIN_PASSWORD_LENGTH) {
    errors.push("temporaryPassword");
  }
  if (user.validationLevel === null) {
    errors.push("validationLevel");
  }
  return errors;
}

/** Whether a step is complete enough for WEITER (and ANLEGEN, for the summary). */
export function isStepComplete(step: SetupStep, draft: ClientSetupDraft, options: ClientSetupOptions | null): boolean {
  switch (step) {
    case "masterData":
      return MASTER_DATA_FIELDS.every((field) => setupMasterDataError(field, draft.masterData[field]) === null) &&
        isEmailAddress(draft.email) &&
        draft.password.length >= MIN_PASSWORD_LENGTH &&
        (draft.groupKind !== "MEMBER" || draft.masterClientId !== null);
    case "products":
      return true;
    case "projectTypes":
      return followsMaster(draft) || possibleProjectTypeIds(options, draft.products).length === 0 || draft.projectTypeIds.length > 0;
    case "projectOrganisation":
      return followsMaster(draft) || PROJECT_ORGANISATION_QUESTIONS.every((question) =>
        MAY_BE_NOWHERE.includes(question) || draft.projectOrganisation[question].length > 0
      );
    case "functions":
      return true;
    case "licences":
      return neededLicences(draft.products).every((key) => licenceCount(draft.licences[key]) !== null);
    case "contracts":
      return true;
    case "users":
      return !draft.withFirstUser || firstUserErrors(draft.firstUser).length === 0;
    case "externalSystems":
      return !draft.products.interface || followsMaster(draft) || setupExternalSystemErrors(draft).length === 0;
    case "summary":
      return SETUP_STEPS.filter((other) => other !== "summary").every((other) => isStepComplete(other, draft, options));
  }
}

/** The counts to send: the typed ones the products need, 0 for the rest. */
function licencesToSend(draft: ClientSetupDraft): ClientSetupLicences {
  const needed = neededLicences(draft.products);
  const counts = { basic: 0, damageAssessmentAndLeakDetection: 0, dryingAndRepair: 0 };
  for (const key of CLIENT_SETUP_LICENCE_KEYS) {
    counts[key] = needed.includes(key) ? licenceCount(draft.licences[key]) ?? 0 : 0;
  }
  return counts;
}

/** The request ANLEGEN sends. A member sends no settings, Verträge or update conditions; it follows its master. */
export function toClientSetupRequest(draft: ClientSetupDraft): ClientSetupRequest {
  const masterData = {} as Record<MasterDataField, string | null>;
  for (const field of MASTER_DATA_FIELDS) {
    masterData[field] = masterDataValue(field, draft.masterData[field]);
  }
  const member = followsMaster(draft);
  const user = draft.firstUser;
  const system = draft.externalSystem;
  const createsSystem = draft.products.interface && !member && draft.withExternalSystem;

  return {
    masterData,
    email: draft.email.trim().toLowerCase(),
    password: draft.password,
    notLiveReason: draft.notLiveReason,
    group: { kind: draft.groupKind, masterClientId: member ? draft.masterClientId : null },
    products: { ...draft.products },
    projectTypeIds: member ? null : [...draft.projectTypeIds],
    projectOrganisation: member ? null : {
      newProjectsIn: [...draft.projectOrganisation.newProjectsIn],
      editProjectsIn: [...draft.projectOrganisation.editProjectsIn],
      finaliseProjectsIn: [...draft.projectOrganisation.finaliseProjectsIn],
      newAppointmentsIn: [...draft.projectOrganisation.newAppointmentsIn],
    },
    functions: member ? null : { ...draft.functions },
    licences: licencesToSend(draft),
    contracts: member ? null : {
      ...draft.contracts,
      contractTypes: [...draft.contracts.contractTypes],
      // A date kept while its contract was unticked is not sent, as the Verträge card does.
      hasServiceContractForFirmwaresAndConfigsSince: draft.contracts.hasServiceContractForFirmwaresAndConfigs
        ? draft.contracts.hasServiceContractForFirmwaresAndConfigsSince
        : null,
      hasWarrantyExtensionForPanelsSince: draft.contracts.hasWarrantyExtensionForPanels ? draft.contracts.hasWarrantyExtensionForPanelsSince : null,
    },
    deviceUpdates: member ? null : { ...draft.deviceUpdates },
    firstUser: draft.withFirstUser && user.validationLevel !== null
      ? {
        firstName: user.firstName.trim(),
        lastName: user.lastName.trim(),
        nickname: user.nickname.trim(),
        staffNumber: user.staffNumber.trim(),
        emailAddress: user.emailAddress.trim().toLowerCase(),
        temporaryPassword: user.temporaryPassword,
        validationLevel: user.validationLevel,
      }
      : null,
    externalSystem: createsSystem
      ? {
        name: system.name.trim(),
        email: setupExternalSystemEmail(draft).trim().toLowerCase(),
        system: system.system,
        signIn: system.system === "EXTERNAL" ? system.signIn : null,
        baseUrl: system.system === "NONE" || system.baseUrl.trim() === "" ? null : system.baseUrl.trim(),
      }
      : null,
    linkedExternalSystemIds: draft.products.interface && member ? [...draft.linkedExternalSystemIds] : [],
  };
}

/** Whether anything was entered, so leaving the page asks first. */
export function hasSetupInput(draft: ClientSetupDraft): boolean {
  return JSON.stringify(draft) !== JSON.stringify(createClientSetupDraft());
}

/** The i18n key for IRES Cloud's refusal (`details.reason`), or the general failure. */
export function clientSetupErrorKey(reason: unknown): string {
  return typeof reason === "string" && CLIENT_SETUP_REFUSALS.includes(reason as ClientSetupRefusal)
    ? `admin.clients.setup.errors.${reason}`
    : "admin.clients.setup.errors.FAILED";
}
